import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Badge } from '../../../components/ui/badge';
import {
  Search, Monitor, Power, PowerOff, Pause, Save, Link2,
  ChevronLeft, ChevronRight, Filter, X, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { toast } from 'sonner';

const formatSize = (gb) => {
  if (!gb) return '-';
  const n = Number(gb);
  if (n >= 1024) return `${(n / 1024).toFixed(1)} TB`;
  return `${n.toFixed(0)} GB`;
};

const formatIp = (raw) => {
  if (!raw) return '-';
  const s = String(raw).trim();
  if (s === '{}' || s === '[]' || s === '') return '-';
  try {
    const p = JSON.parse(s);
    if (Array.isArray(p)) return p.filter(Boolean).join(', ') || '-';
    if (p && typeof p === 'object') { const v = Object.values(p).flat().filter(Boolean); return v.length ? v.join(', ') : '-'; }
    return String(p) || '-';
  } catch { return s; }
};

const HypervVMs = () => {
  const [loading, setLoading] = useState(true);
  const [vms, setVMs] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', state: '', has_server: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'name', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const fetchVMs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.state) params.append('state', filters.state);
      if (filters.has_server === 'yes') params.append('has_server', 'true');
      if (filters.has_server === 'no') params.append('has_server', 'false');
      const res = await api.get(`/virt/hyperv/vms?${params}`);
      setVMs(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar VMs Hyper-V');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try { const res = await api.get('/virt/hyperv/vms/stats'); setStats(res.data); }
    catch (err) { console.error(err); }
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchVMs, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  const handleSort = (field) => setSortConfig(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ field }) => sortConfig.field !== field
    ? <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />
    : (sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />);
  const SortableHeader = ({ field, children, className = '' }) => (
    <th className={`cursor-pointer hover:bg-muted/70 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{children}<SortIcon field={field} /></div>
    </th>
  );

  const handleStatFilter = (t) => {
    setActiveStatFilter(t);
    setPagination(prev => ({ ...prev, page: 1 }));
    switch (t) {
      case 'running': setFilters({ search: '', state: 'running', has_server: '' }); break;
      case 'off': setFilters({ search: '', state: 'off', has_server: '' }); break;
      case 'with_server': setFilters({ search: '', state: '', has_server: 'yes' }); break;
      case 'without_server': setFilters({ search: '', state: '', has_server: 'no' }); break;
      default: setFilters({ search: '', state: '', has_server: '' }); break;
    }
  };

  const getStateIcon = (s) => {
    switch (s) {
      case 'running': return <Power className="h-4 w-4 text-green-500" />;
      case 'off': return <PowerOff className="h-4 w-4 text-red-500" />;
      case 'saved': return <Save className="h-4 w-4 text-blue-500" />;
      case 'paused': return <Pause className="h-4 w-4 text-yellow-500" />;
      default: return <Monitor className="h-4 w-4" />;
    }
  };
  const getStateLabel = (s) => ({ running: 'Encendida', off: 'Apagada', saved: 'Guardada', paused: 'Pausada' }[s] || s || '-');

  const getISBadge = (state) => {
    if (!state) return <span className="text-muted-foreground">-</span>;
    if (state === 'Ok') return <Badge variant="outline" className="text-green-500 border-green-500">OK</Badge>;
    return <Badge variant="outline" className="text-yellow-500 border-yellow-500">{state}</Badge>;
  };

  const clearFilters = () => { setFilters({ search: '', state: '', has_server: '' }); setActiveStatFilter('all'); };
  const hasFilters = filters.search || filters.state || filters.has_server;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="page-title">Máquinas Virtuales (Hyper-V)</h1>
          <p className="page-subtitle">Inventario de VMs Hyper-V con relación a servidores físicos</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <Card className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`} onClick={() => handleStatFilter('all')} data-testid="stat-total"><CardContent className="p-4 text-center"><p className="stats-card-value">{stats.total || 0}</p><p className="stats-card-label">Total VMs</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-green-500/50 ${activeStatFilter === 'running' ? 'border-green-500 ring-1 ring-green-500/20' : ''}`} onClick={() => handleStatFilter('running')} data-testid="stat-running"><CardContent className="p-4 text-center"><p className="stats-card-value text-green-500">{stats.running || 0}</p><p className="stats-card-label">Encendidas</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-red-500/50 ${activeStatFilter === 'off' ? 'border-red-500 ring-1 ring-red-500/20' : ''}`} onClick={() => handleStatFilter('off')} data-testid="stat-off"><CardContent className="p-4 text-center"><p className="stats-card-value text-red-500">{stats.off_count || 0}</p><p className="stats-card-label">Apagadas</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-blue-500/50 ${activeStatFilter === 'with_server' ? 'border-blue-500 ring-1 ring-blue-500/20' : ''}`} onClick={() => handleStatFilter('with_server')} data-testid="stat-with-server"><CardContent className="p-4 text-center"><p className="stats-card-value text-blue-500">{stats.with_server || 0}</p><p className="stats-card-label">Con Servidor</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-yellow-500/50 ${activeStatFilter === 'without_server' ? 'border-yellow-500 ring-1 ring-yellow-500/20' : ''}`} onClick={() => handleStatFilter('without_server')} data-testid="stat-without-server"><CardContent className="p-4 text-center"><p className="stats-card-value text-yellow-500">{stats.without_server || 0}</p><p className="stats-card-label">Sin Servidor</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="stats-card-value">{formatSize(stats.total_ram_gb)}</p><p className="stats-card-label">RAM Total</p></CardContent></Card>
          </div>
        )}

        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Filtros:</span></div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar VM, IP, SO, servidor..." value={filters.search}
                  onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setActiveStatFilter(''); }}
                  className="h-9" data-testid="vm-search-input" />
              </div>
              <Select value={filters.state || 'todos'} onValueChange={(v) => { setFilters(prev => ({ ...prev, state: v === 'todos' ? '' : v })); setActiveStatFilter(''); }}>
                <SelectTrigger className="w-36 h-9" data-testid="vm-state-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="running">Encendida</SelectItem>
                  <SelectItem value="off">Apagada</SelectItem>
                  <SelectItem value="saved">Guardada</SelectItem>
                  <SelectItem value="paused">Pausada</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.has_server || 'todos'} onValueChange={(v) => { setFilters(prev => ({ ...prev, has_server: v === 'todos' ? '' : v })); setActiveStatFilter(''); }}>
                <SelectTrigger className="w-40 h-9" data-testid="vm-server-filter"><SelectValue placeholder="Servidor" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="yes">Con servidor</SelectItem>
                  <SelectItem value="no">Sin servidor</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" /> Limpiar</Button>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="vms-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="state" className="text-center justify-center">ESTADO</SortableHeader>
                  <SortableHeader field="vcpu" className="text-center justify-center">vCPU</SortableHeader>
                  <SortableHeader field="ram_assigned_gb" className="text-center justify-center">RAM</SortableHeader>
                  <SortableHeader field="disk_total_gb" className="text-center justify-center">DISCO</SortableHeader>
                  <SortableHeader field="ip_address" className="text-left">IP</SortableHeader>
                  <SortableHeader field="host_hostname" className="text-left">HOST</SortableHeader>
                  <SortableHeader field="server_hostname" className="text-left">SERVIDOR</SortableHeader>
                  <th>INT. SERVICES</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : vms.length === 0 ? (
                  <tr><td colSpan={9} className="text-center p-8 text-muted-foreground">No hay VMs registradas</td></tr>
                ) : vms.map(vm => (
                  <tr key={vm.id} className="border-b hover:bg-muted/30" data-testid={`vm-row-${vm.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{vm.name}</span>
                        {vm.generation && <Badge variant="secondary" className="ml-1 text-xs">Gen{vm.generation}</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground ml-6">{vm.guest_os || vm.hostname_guest || '-'}</p>
                    </td>
                    <td className="p-4 text-center"><div className="flex items-center justify-center gap-1">{getStateIcon(vm.state)}<span className="text-xs">{getStateLabel(vm.state)}</span></div></td>
                    <td className="p-4 text-center">{vm.vcpu || '-'}</td>
                    <td className="p-4 text-center">
                      {vm.ram_assigned_gb ? `${Number(vm.ram_assigned_gb).toFixed(0)} GB` : '-'}
                      {vm.dynamic_memory === 1 && <p className="text-[10px] text-muted-foreground">dinámica</p>}
                    </td>
                    <td className="p-4 text-center">{formatSize(vm.disk_total_gb)}</td>
                    <td className="p-4 font-mono text-xs">{formatIp(vm.ip_address)}</td>
                    <td className="p-4 text-sm text-muted-foreground">{vm.host_hostname || '-'}</td>
                    <td className="p-4">
                      {vm.server_id ? (
                        <Link to={`/assets/servers?search=${encodeURIComponent(vm.server_hostname)}`} className="flex items-center gap-1 text-blue-500 hover:underline text-sm">
                          <Link2 className="h-3 w-3" />{vm.server_hostname}
                        </Link>
                      ) : <span className="text-muted-foreground">-</span>}
                    </td>
                    <td className="p-4">{getISBadge(vm.integration_services_state)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages} ({pagination.total} VMs)</span>
            <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HypervVMs;
